import { ComponentProvider } from '@atoms/ComponentProvider';
import { markComponentKind } from '@atoms/util';
import { CompilationStatusProvider } from '@common/CompilationStatusContext';
import { useHistoricalViewID } from '@common/Configuration/hook';
import { usePipelineStatus } from '@common/git/CodeGeneration/hook';
import { useIdeActions, useSetIdeAction } from '@common/ide/state';
import { UnloadHandler } from '@common/ide/UnloadHandler';
import { LogMessageProvider } from '@common/LogMessageContext';
import { useIsNewPipelineOnboardingEnabled, useIsPipelineOnboardingEnabled } from '@common/onboarding/hooks';
import { Entity } from '@common/types/Entity';
import { EntityEvents } from '@common/user-analytics';
import { useWorkflowExecutionState, WorkflowExecutionProvider } from '@common/workflowExecutionHooks';
import { BaseLSPProvider } from '@LSP/base/BaseLSPContext';
import { DiagnosticProvider } from '@LSP/DiagnosticProvider';
import { useCreateLSPClient } from '@LSP/hook';
import { PipelineLSPClient } from '@LSP/pipeline/client';
import { UnitTestClient } from '@LSP/unit-test/client';
import { LSPUnitTestProvider } from '@LSP/unit-test/LSPUnitTestContext';
import { useEffect, useMemo } from 'react';
import { Provider } from 'react-redux';
import { createGlobalStyle } from 'styled-components';

import { useGlobalVariables } from '../../context/globalConfig/securedConfigHook';
import { useMixPanel } from '../../context/mixpanel/context';
import { useExecutionService } from '../../data/executionHooks';
import { LogProvider, useLoggerState } from '../../generic-components/Logger/RuntimeLogger';
import { useHistory, withHistoryProvider } from '../../HistoryManager/context';
import { getWorkflowEditorWSEndPoint, getWorkflowUnitTestWSEndPoint } from '../../utils/getServerUrl';
import { isWebsocketCompressionEnabled } from '../../utils/localstorage-keys';
import { ComponentRunContext, useRunDataContext } from './AttachFabric/componentRunhook';
import SelectBox from './components/SelectBox';
import TextBox from './components/TextBox';
import { Container } from './Container';
import { useCodeViewNotifications, useUnitTestNotifications, useWorkflowEditor } from './hook';
import WorkflowUploadProvier from './Upload/context';
import { WorkflowDialog, WorkflowDialogFooter, WorkflowDialogTitle } from './WorkflowDialog';

import type { PipelineInitializeParams } from '../../common/ide/PipelineInitializeParamsTypes';

interface WorkflowProps {
  uid: string;
  projectId: string;
  isUIDFromAnotherProject?: boolean;
  hasCodeGeneration?: boolean;
  version?: string;
  branch?: string;
  configurationInstanceId?: string;
  isSelectiveInterimMode?: boolean;
}

const GlobalIDECSS = createGlobalStyle`
	html, body{
	/*To remove the mac gestures to go back to the previous page*/
  overscroll-behavior-x: none;
	}
`;
const overrideComponents = {
  ...markComponentKind({
    'Dialogs.Container': WorkflowDialog,
    'Dialogs.Footer': WorkflowDialogFooter,
    'Dialogs.Title': WorkflowDialogTitle,
    'Atoms.TextBox': TextBox,
    'Atoms.SelectBox': SelectBox
  })
};

const websocketUrl = getWorkflowEditorWSEndPoint();

const PipelineComponent = withHistoryProvider(function Pipeline({
  uid,
  projectId,
  branch,
  hasCodeGeneration,
  configurationInstanceId
}: WorkflowProps) {
  const decodedWorkflowId = decodeURIComponent(uid);

  const { track } = useMixPanel();
  useEffect(() => {
    track(EntityEvents[Entity.Pipeline].ide.openPipeline, { uid: decodedWorkflowId, branch });
  }, [track, branch, decodedWorkflowId]);

  const loggerState = useLoggerState();
  const pipelineRunId = useHistoricalViewID() as string;
  const isRebuildEnabled = useGlobalVariables().isCodeGenerationEnabled;
  const { isCodeGenerating, isCodeGenerationInProgress, codeGenStatus } = usePipelineStatus(uid, hasCodeGeneration);

  const IsCodeGenerationInProgress = Boolean((isCodeGenerating || isCodeGenerationInProgress) && !pipelineRunId);
  const { lspClient } = useCreateLSPClient(() => {
    return new PipelineLSPClient<PipelineInitializeParams>(websocketUrl, {
      compressionEnabled: isWebsocketCompressionEnabled,
      blockCodeView: true,
      lazyInit: IsCodeGenerationInProgress
    });
  });

  const { setIDEActions } = useSetIdeAction();
  const { setPipelineLspClient } = useIdeActions();

  const lspUnitTestClient = useMemo(() => new UnitTestClient(getWorkflowUnitTestWSEndPoint()), []);
  const executionService = useExecutionService();

  useEffect(() => {
    if (!IsCodeGenerationInProgress && isRebuildEnabled) {
      lspClient.lazyInit();
    }
  }, [IsCodeGenerationInProgress, lspClient, isRebuildEnabled]);

  const { workflowStore, init, pipelineNotFound } = useWorkflowEditor({
    lspClient,
    workflowId: decodedWorkflowId,
    pipelineRunId,
    configurationInstanceId
  });
  const workflowExecutionState = useWorkflowExecutionState(
    uid,
    executionService,
    lspClient,
    loggerState.logger,
    loggerState.logs
  );
  useCodeViewNotifications(lspClient, workflowStore.dispatch, decodedWorkflowId);
  useUnitTestNotifications(lspUnitTestClient, workflowStore.dispatch);

  useEffect(() => {
    setIDEActions({
      reinitializeLSPconnection: lspClient.reconnect
    });
  }, [setIDEActions, lspClient]);

  useEffect(() => {
    setPipelineLspClient(lspClient);
    return () => {
      // disconnect websocket on un-mount
      lspClient.detach();
      lspUnitTestClient.detach();
      setPipelineLspClient(undefined);
    };
  }, [lspClient, lspUnitTestClient, setPipelineLspClient]);

  const history = useHistory();
  useEffect(() => {
    history?.reset();
  }, [history, uid]);

  const runDataContext = useRunDataContext(pipelineRunId);

  return (
    <LSPUnitTestProvider lspUnitTestClient={lspUnitTestClient}>
      <BaseLSPProvider client={lspClient}>
        <Provider store={workflowStore}>
          <DiagnosticProvider>
            <GlobalIDECSS />
            <WorkflowExecutionProvider value={workflowExecutionState}>
              <UnloadHandler />
              <LogProvider value={loggerState}>
                <WorkflowUploadProvier>
                  <ComponentProvider value={overrideComponents}>
                    <ComponentRunContext.Provider value={runDataContext}>
                      <Container
                        uid={uid}
                        initialize={init}
                        codeGenStatus={isCodeGenerationInProgress && !pipelineRunId ? codeGenStatus : undefined}
                        projectId={projectId}
                        pipelineNotFound={pipelineNotFound.current}
                      />
                    </ComponentRunContext.Provider>
                  </ComponentProvider>
                </WorkflowUploadProvier>
              </LogProvider>
            </WorkflowExecutionProvider>
          </DiagnosticProvider>
        </Provider>
      </BaseLSPProvider>
    </LSPUnitTestProvider>
  );
});

export const Pipeline = (props: WorkflowProps) => {
  const isPipelineOnboardingEnabled = useIsPipelineOnboardingEnabled();
  const isNewPipelineOnboardingEnabled = useIsNewPipelineOnboardingEnabled();
  return (
    <CompilationStatusProvider>
      <LogMessageProvider>
        <PipelineComponent
          {...props}
          hasCodeGeneration={
            !isPipelineOnboardingEnabled &&
            !isNewPipelineOnboardingEnabled &&
            !props.isUIDFromAnotherProject &&
            !props.configurationInstanceId
          }
          isHistoryEnabled={!isPipelineOnboardingEnabled && !isNewPipelineOnboardingEnabled}
        />
      </LogMessageProvider>
    </CompilationStatusProvider>
  );
};
